import { useState, useCallback, useEffect } from 'react'
import { aiService, ChatMessage } from '@services/aiService'
import { useAuthStore } from '@store/useAuthStore'
import api from '@services/api'

// Timeout longo para o chat da IA: o backend pode demorar até 120s a obter resposta do modelo
const CHAT_AI_TIMEOUT_MS = 120000
// Se o pedido der timeout, voltamos a pedir a conversa de X em X ms até a resposta aparecer
const POLL_INTERVAL_MS = 4000
const POLL_MAX_ATTEMPTS = 45 // ~3 minutos

export interface WorkOrderContext {
  workOrderId?: number
  vehicleId?: number
  customerCompanyId?: number
  workshopCompanyId?: number
  vehicleInfo?: string
  customerName?: string
  // Informações sobre peças e preços para análise pela IA
  pieceItems?: Array<{
    id?: number
    pieceDescription?: string
    quantity?: number
    unitPrice?: number
    subtotal?: number
    notes?: string
  }>
  serviceItems?: Array<{
    id?: number
    serviceDescription?: string
    hours?: number
    hourlyRate?: number
    subtotal?: number
    notes?: string
  }>
  partsCost?: number
  laborCost?: number
  totalCost?: number
  finalAmount?: number
}

export interface SolutionOption {
  id?: number
  optionNumber: number
  diagnosis: string
  description: string
  estimatedHours?: number
  estimatedCost?: number
  successProbability?: number
  partsNeeded?: string
  servicesNeeded?: string
  status?: number // 1 = pendente, 2 = confirmada, 3 = rejeitada
  conversationId?: number
}

export function useAIChat(workOrderContext?: WorkOrderContext) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: workOrderContext?.workOrderId
        ? 'Está com problemas? Posso ajudá-lo com um passo a passo para esta viatura.'
        : 'Está com problemas? Posso ajudá-lo com um passo a passo para esta viatura.',
      timestamp: new Date().toISOString(),
    },
  ])
  const [isLoading, setIsLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [solutionOptions, setSolutionOptions] = useState<SolutionOption[]>([])
  const { user } = useAuthStore()

  // Minimum delay to simulate "thinking" (1 second)
  const MIN_DELAY_MS = 1000

  // Note: Conversation is now saved automatically by the backend when sending messages
  // No need for separate saveConversationToBackend function

  // CRÍTICO: Carregar conversa existente quando o componente é montado
  useEffect(() => {
    const loadExistingConversation = async () => {
      if (!workOrderContext?.workOrderId) {
        return
      }

      try {
        console.log('🔍 Buscando conversas para workOrderId:', workOrderContext.workOrderId)
        // Buscar conversas existentes para esta work order
        const response = await api.get<{ data: Array<{ conversationId?: number; id?: number; conversation_id?: number; messages?: any[] }> }>(
          `/chat/mechanic/work-order/${workOrderContext.workOrderId}`,
          { timeout: CHAT_AI_TIMEOUT_MS }
        )

        console.log('📥 Resposta completa:', response.data)
        const conversations = response.data?.data || []
        
        console.log('📋 Conversas encontradas:', conversations.length)
        if (conversations.length > 0) {
          console.log('📋 Estrutura da primeira conversa:', JSON.stringify(conversations[0], null, 2))
        }
        
        // Se houver conversas, usar a mais recente (primeira da lista, assumindo que vem ordenada)
        if (conversations.length > 0) {
          // Ordenar por dtChanged (mais recente primeiro) se disponível
          const sortedConversations = [...conversations].sort((a, b) => {
            const dateA = (a as any).dtChanged ? new Date((a as any).dtChanged).getTime() : 0
            const dateB = (b as any).dtChanged ? new Date((b as any).dtChanged).getTime() : 0
            return dateB - dateA
          })
          
          const latestConversation = sortedConversations[0]
          // Tentar múltiplos campos possíveis
          const conversationId = latestConversation.conversationId || latestConversation.id || latestConversation.conversation_id
          
          console.log('📋 conversationId extraído:', conversationId)
          
          if (!conversationId) {
            console.error('❌ conversationId não encontrado na resposta! Estrutura:', latestConversation)
            return
          }

          // Buscar mensagens completas da conversa
          console.log('📥 Buscando mensagens da conversa:', conversationId)
          try {
            const conversationResponse = await api.get<{ data: { conversationId: number; messages: any[] } }>(
              `/chat/mechanic/${conversationId}`,
              { timeout: CHAT_AI_TIMEOUT_MS }
            )

            console.log('📥 Resposta da conversa:', conversationResponse.data)
            const conversationData = conversationResponse.data?.data
            
            if (!conversationData) {
              console.error('❌ conversationData é null ou undefined!')
              // Limpar conversationId inválido
              setConversationId(null)
              return
            }
          
            if (conversationData && conversationData.messages) {
            // Converter mensagens do backend (ChatMessagePair) para o formato do frontend
            // ChatMessagePair: question = assistant, answer = user
            // Cada pair representa uma mensagem (não um par de mensagens)
            const loadedMessages: ChatMessage[] = conversationData.messages
              .map((pair: any) => {
                if (pair.answer) {
                  // Mensagem do usuário
                  return {
                    role: 'user' as const,
                    content: pair.answer,
                    timestamp: new Date().toISOString(),
                    _alreadyDisplayed: true,
                    _isHistorical: true,
                    position: pair.position || 0,
                  }
                } else if (pair.question) {
                  // Mensagem do assistente
                  return {
                    role: 'assistant' as const,
                    content: pair.question,
                    timestamp: new Date().toISOString(),
                    messageId: pair.messageId,
                    feedbackUsed: pair.feedbackUsed,
                    feedbackWorked: pair.feedbackWorked,
                    _alreadyDisplayed: true,
                    _isHistorical: true,
                    position: pair.position || 0,
                  }
                }
                return null
              })
              .filter((msg): msg is NonNullable<typeof msg> => msg !== null)
              .sort((a, b) => ((a as ChatMessage).position || 0) - ((b as ChatMessage).position || 0)) as ChatMessage[]

            // Se não houver mensagens, manter a mensagem inicial padrão
            if (loadedMessages.length > 0) {
              setMessages(loadedMessages)
              console.log(`✅ ${loadedMessages.length} mensagens carregadas`)
            } else {
              console.log('ℹ️ Conversa encontrada mas sem mensagens, mantendo mensagem inicial')
            }
            
            setConversationId(conversationId)
            console.log(`✅ Conversa existente carregada: conversationId=${conversationId}, mensagens=${loadedMessages.length}`)
            
            // Carregar solution options se houver
            try {
              const optionsResponse = await api.get<{ data: any[] }>(
                `/chat/mechanic/${conversationId}/solution-options`,
                { timeout: CHAT_AI_TIMEOUT_MS }
              )
              if (optionsResponse.data?.data && optionsResponse.data.data.length > 0) {
                const options = optionsResponse.data.data.map((opt: any) => ({
                  id: opt.id,
                  optionNumber: opt.optionNumber,
                  diagnosis: opt.diagnosis,
                  description: opt.description,
                  estimatedHours: opt.estimatedHours,
                  estimatedCost: opt.estimatedCost,
                  successProbability: opt.successProbability,
                  partsNeeded: opt.partsNeeded,
                  servicesNeeded: opt.servicesNeeded,
                  status: opt.status,
                  conversationId: conversationId,
                }))
                setSolutionOptions(options)
                console.log(`✅ ${options.length} solution options carregadas`)
              }
            } catch (error) {
              // Não é erro se não houver options
              console.log('ℹ️ Nenhuma solution option encontrada')
            }
          }
          } catch (conversationError: any) {
            // Se a conversa não existe (404), limpar conversationId e permitir criar nova
            if (conversationError.response?.status === 404 || conversationError.response?.status === 500) {
              console.warn('⚠️ Conversa não encontrada ao carregar:', conversationId)
              console.warn('⚠️ Limpando conversationId inválido e permitindo criar nova conversa')
              setConversationId(null)
            } else {
              console.error('❌ Erro ao buscar conversa:', conversationError)
              // Limpar conversationId em caso de erro para evitar usar ID inválido
              setConversationId(null)
            }
            return
          }
        } else {
          console.log('ℹ️ Nenhuma conversa existente encontrada para esta work order')
        }
      } catch (error: any) {
        // Se não encontrar conversas (404), é normal - significa que é a primeira vez
        if (error.response?.status === 404) {
          console.log('ℹ️ Nenhuma conversa existente (primeira vez)')
        } else {
          console.error('❌ Erro ao carregar conversa existente:', error)
          console.error('❌ Detalhes do erro:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            stack: error.stack
          })
          // Não bloquear o chat se houver erro - permitir que o usuário comece uma nova conversa
        }
      }
    }

    if (workOrderContext?.workOrderId) {
      console.log('🚀 Iniciando carregamento de conversa para workOrderId:', workOrderContext.workOrderId)
      loadExistingConversation()
    } else {
      console.log('ℹ️ Sem workOrderId, não carregando conversa existente')
    }
  }, [workOrderContext?.workOrderId]) // Executar apenas quando workOrderId mudar

  const saveSolutionOptions = useCallback(async (options: any[]) => {
    if (!conversationId) return

    try {
      const createdBy = user?.email || user?.name || 'system'
      const response = await api.post(`/chat/mechanic/${conversationId}/solution-options`, {
        options: options.map((opt, idx) => ({
          optionNumber: opt.option_number || idx + 1,
          diagnosis: opt.diagnosis || '',
          description: opt.description || '',
          estimatedHours: opt.estimated_hours || opt.estimatedHours,
          estimatedCost: opt.estimated_cost || opt.estimatedCost,
          successProbability: opt.success_probability || opt.successProbability,
          partsNeeded: opt.parts_needed || opt.partsNeeded,
          servicesNeeded: opt.services_needed || opt.servicesNeeded,
        })),
        createdBy,
      }, { timeout: CHAT_AI_TIMEOUT_MS })

      // Update state with real IDs from backend
      if (response.data?.data?.options) {
        setSolutionOptions(response.data.data.options)
      }
    } catch (error) {
      console.error('Erro ao salvar opções:', error)
    }
  }, [conversationId, user])

  // Helper: Check if user message indicates rejection of all options
  const checkAndHandleRejection = useCallback(async (content: string) => {
    const userMessageLower = content.trim().toLowerCase()
    const rejectionKeywords = [
      'nenhuma opção',
      'nenhuma das opções',
      'nenhuma me parece',
      'nenhuma está correta',
      'nenhuma está certa',
      'todas estão erradas',
      'nenhuma serve',
      'rejeito todas',
      'descarto todas',
      'nenhuma opção me parece viável',
      'nenhuma das opções me parece viável',
      'nenhuma me parece viável',
    ]
    
    const hasPendingOptions = solutionOptions.some(opt => (opt.status || 1) === 1)
    const isRejection = rejectionKeywords.some(keyword => userMessageLower.includes(keyword))
    
    if (isRejection && conversationId && hasPendingOptions) {
      try {
        await api.put(`/chat/mechanic/${conversationId}/solution-options/reject-all`, undefined, { timeout: CHAT_AI_TIMEOUT_MS })
        setSolutionOptions((prev) =>
          prev.map((opt) => (opt.status === 1 ? { ...opt, status: 3, isSelected: false } : opt))
        )
      } catch (error) {
        console.error('Erro ao rejeitar opções:', error)
      }
    }
  }, [conversationId, solutionOptions])

  // Helper: Send message to backend (new or existing conversation)
  const sendMessageToBackend = useCallback(async (content: string) => {
    if (!workOrderContext?.workOrderId) {
      throw new Error('Work order context is required for mechanic chat')
    }

    const createdBy = user?.email || user?.name || 'system'
    
    if (!conversationId) {
      console.log('sendMessageToBackend: Criando nova conversa (startConversation)')
      console.log('sendMessageToBackend: workOrderId:', workOrderContext.workOrderId)
      console.log('sendMessageToBackend: message:', content.trim())
      console.log('sendMessageToBackend: createdBy:', createdBy)
      
      try {
        const requestPayload = {
          workOrderId: workOrderContext.workOrderId,
          message: content.trim(),
          createdBy,
        }
        console.log('sendMessageToBackend: 📤 Enviando POST para /chat/mechanic/start')
        console.log('sendMessageToBackend: Payload:', JSON.stringify(requestPayload, null, 2))
        
        const startResponse = await api.post<{ data: { conversationId: number; messages: any[] } }>('/chat/mechanic/start', requestPayload, { timeout: CHAT_AI_TIMEOUT_MS })
        
        console.log('sendMessageToBackend: ✅ Resposta recebida do servidor')
        console.log('sendMessageToBackend: Status:', startResponse.status)
        console.log('sendMessageToBackend: Headers:', startResponse.headers)
        console.log('sendMessageToBackend: Data completa:', JSON.stringify(startResponse.data, null, 2))
        console.log('sendMessageToBackend: conversationId recebido:', startResponse.data?.data?.conversationId)
        console.log('sendMessageToBackend: mensagens recebidas:', startResponse.data?.data?.messages?.length)
        
        if (!startResponse.data || !startResponse.data.data) {
          console.error('sendMessageToBackend: ❌ Resposta não tem estrutura esperada!')
          throw new Error('Resposta inválida do servidor: estrutura de dados incorreta')
        }
        
        if (startResponse.data.data.conversationId) {
          setConversationId(startResponse.data.data.conversationId)
          console.log('sendMessageToBackend: ✅ conversationId salvo no estado:', startResponse.data.data.conversationId)
        } else {
          console.warn('sendMessageToBackend: ⚠️ Resposta não contém conversationId!')
        }
        
        return startResponse.data.data
      } catch (error: any) {
        console.error('sendMessageToBackend: ❌ Erro ao criar conversa:', error)
        console.error('sendMessageToBackend: Status:', error.response?.status)
        console.error('sendMessageToBackend: Data:', error.response?.data)
        throw error
      }
    } else {
      console.log('sendMessageToBackend: Adicionando mensagem à conversa existente:', conversationId)
      try {
        const messageResponse = await api.post<{ data: { conversationId: number; messages: any[] } }>(
          `/chat/mechanic/${conversationId}/message`,
          {
            message: content.trim(),
            createdBy,
          },
          { timeout: CHAT_AI_TIMEOUT_MS }
        )
        console.log('sendMessageToBackend: ✅ Resposta do addMessage:', messageResponse.data)
        console.log('sendMessageToBackend: Estrutura completa:', JSON.stringify(messageResponse.data, null, 2))
        console.log('sendMessageToBackend: messageResponse.data.data:', messageResponse.data.data)
        console.log('sendMessageToBackend: messageResponse.data.data.messages:', messageResponse.data.data?.messages)
        console.log('sendMessageToBackend: Número de mensagens:', messageResponse.data.data?.messages?.length)
        return messageResponse.data.data
      } catch (error: any) {
        console.error('sendMessageToBackend: ❌ Erro ao adicionar mensagem:', error)
        
        // Se a conversa não existe (404), criar uma nova conversa
        if (error.response?.status === 404 || error.response?.status === 500) {
          const errorMessage = error.response?.data?.message || ''
          if (errorMessage.includes('Conversa não encontrada') || errorMessage.includes('não encontrada')) {
            console.log('sendMessageToBackend: ⚠️ Conversa não encontrada, criando nova conversa...')
            // Limpar conversationId para forçar criação de nova conversa
            setConversationId(null)
            // Recriar a conversa diretamente (não recursivo)
            if (workOrderContext?.workOrderId) {
              try {
                const startResponse = await api.post<{ data: { conversationId: number; messages: any[] } }>(
                  '/chat/mechanic/start',
                  {
                    workOrderId: workOrderContext.workOrderId,
                    message: content.trim(),
                    createdBy,
                  },
                  { timeout: CHAT_AI_TIMEOUT_MS }
                )
                console.log('sendMessageToBackend: ✅ Nova conversa criada:', startResponse.data.data.conversationId)
                if (startResponse.data.data.conversationId) {
                  setConversationId(startResponse.data.data.conversationId)
                }
                return startResponse.data.data
              } catch (retryError: any) {
                console.error('sendMessageToBackend: ❌ Erro ao criar nova conversa:', retryError)
                throw retryError
              }
            }
          }
        }
        
        throw error
      }
    }
  }, [workOrderContext, user, conversationId])

  // Helper: Convert a single ChatMessagePair to ChatMessage[]
  const convertMessagePair = useCallback((pair: any): ChatMessage[] => {
    const messages: ChatMessage[] = []
    
    console.log('convertMessagePair: Processando pair:', {
      position: pair.position,
      question: pair.question?.substring(0, 50) || 'null',
      answer: pair.answer?.substring(0, 50) || 'null',
      questionLength: pair.question?.length || 0,
      answerLength: pair.answer?.length || 0,
      messageId: pair.messageId
    })
    
    // CRÍTICO: ChatMessagePair pode ter question (assistant) OU answer (user)
    // A ordem importa: se ambos existirem, answer vem primeiro (user), depois question (assistant)
    // Mas na prática, cada pair tem apenas um deles preenchido
    
    if (pair.answer?.trim()) {
      // Mensagem do usuário
      console.log('convertMessagePair: Adicionando mensagem do usuário:', pair.answer.trim().substring(0, 50))
      messages.push({
        role: 'user',
        content: pair.answer.trim(),
        timestamp: new Date().toISOString(),
        _alreadyDisplayed: true, // Mensagens do usuário sempre aparecem imediatamente
        _isHistorical: false,
      })
    }
    
    if (pair.question?.trim()) {
      // Mensagem do assistente
      console.log('convertMessagePair: Adicionando mensagem do assistente:', pair.question.trim().substring(0, 50))
      messages.push({
        role: 'assistant',
        content: pair.question.trim(),
        timestamp: new Date().toISOString(),
        messageId: pair.messageId,
        feedbackUsed: pair.feedbackUsed,
        feedbackWorked: pair.feedbackWorked,
        _alreadyDisplayed: false, // Será determinado depois se é histórico ou novo
        _isHistorical: false,
      })
    } else {
      console.warn('convertMessagePair: ⚠️ Pair não tem question (assistant) preenchido!', pair)
    }
    
    console.log('convertMessagePair: Retornando', messages.length, 'mensagens')
    return messages
  }, [])

  // Helper: Convert backend ChatMessagePair[] to ChatMessage[]
  const convertBackendMessages = useCallback((backendPairs: any[]): ChatMessage[] => {
    if (!backendPairs || !Array.isArray(backendPairs)) {
      console.warn('convertBackendMessages: backendPairs não é um array válido:', backendPairs)
      return []
    }
    
    console.log('convertBackendMessages: Convertendo', backendPairs.length, 'pairs')
    backendPairs.forEach((pair, idx) => {
      console.log(`convertBackendMessages: Pair ${idx}: position=${pair.position}, question=${pair.question?.substring(0, 30) || 'null'}, answer=${pair.answer?.substring(0, 30) || 'null'}`)
    })
    
    const sortedPairs = [...backendPairs].sort((a: any, b: any) => 
      (a.position || 0) - (b.position || 0)
    )
    
    const converted = sortedPairs.flatMap((pair) => {
      const messages = convertMessagePair(pair)
      console.log(`convertBackendMessages: Pair na posição ${pair.position} convertido para ${messages.length} mensagens`)
      return messages
    })
    
    console.log('convertBackendMessages: Total de mensagens convertidas:', converted.length)
    converted.forEach((msg, idx) => {
      console.log(`convertBackendMessages: Mensagem ${idx}: role=${msg.role}, content=${msg.content?.substring(0, 50)}...`)
    })
    
    return converted
  }, [convertMessagePair])

  // Helper: Create message key for tracking displayed messages (stable across reordering)
  const createMessageKey = useCallback((msg: ChatMessage): string => {
    return `${(msg as any).messageId ?? msg.timestamp}`
  }, [])

  // Helper: Build set of existing message keys
  const buildExistingMessageKeys = useCallback((prevMessages: ChatMessage[]): Set<string> => {
    return new Set(
      prevMessages
        .filter((msg) => msg.role === 'assistant' && msg.timestamp)
        .map((msg) => createMessageKey(msg))
    )
  }, [createMessageKey])

  // Helper: Update messages state preserving display flags
  // Backend returns ALL messages, so we should use them directly (not merge)
  // But we need to preserve _alreadyDisplayed flags for messages that were already shown
  const updateMessagesWithBackendData = useCallback((backendMessages: ChatMessage[], prevMessages: ChatMessage[]) => {
    const existingKeys = buildExistingMessageKeys(prevMessages)
    
    // Backend returns complete message history, so use it directly
    // But preserve display flags for messages that were already shown
    // CRÍTICO: Garantir que todas as mensagens (user e assistant) sejam preservadas
    const updatedMessages = backendMessages.map((msg) => {
      if (msg.role === 'assistant' && msg.timestamp) {
      const messageKey = createMessageKey(msg)
        if (existingKeys.has(messageKey)) {
          // Mensagem já foi exibida - marcar como já exibida e usar typewriter rápido
          return { ...msg, _alreadyDisplayed: true, _isHistorical: true }
        } else {
          // Nova mensagem da IA - usar typewriter normal
          return { ...msg, _alreadyDisplayed: false, _isHistorical: false }
        }
      } else if (msg.role === 'user') {
        // Mensagens do usuário sempre aparecem imediatamente (sem typewriter)
        return { ...msg, _alreadyDisplayed: true, _isHistorical: true }
      }
      return msg
    })
    
    // Garantir que não perdemos nenhuma mensagem
    if (updatedMessages.length === 0 && prevMessages.length > 0) {
      console.warn('⚠️ Backend retornou 0 mensagens, mantendo mensagens anteriores')
      return prevMessages
    }
    
    return updatedMessages
  }, [buildExistingMessageKeys, createMessageKey])

  // Helper: Check if message contains solution options
  const messageHasOptions = useCallback((content: string): boolean => {
    const lowerContent = content.toLowerCase()
    return lowerContent.includes('opção')
  }, [])

  // Helper: Extract and save solution options if present
  const extractAndSaveOptions = useCallback(async (backendMessages: ChatMessage[], latestAIMessage: ChatMessage | undefined) => {
    if (!latestAIMessage || !conversationId) return
    if (!messageHasOptions(latestAIMessage.content)) return
    
    try {
      const extracted = await aiService.extractWorkOrderData(backendMessages)
      if (extracted.options && extracted.options.length > 0) {
        await saveSolutionOptions(extracted.options)
      }
    } catch (error) {
      console.error('Erro ao extrair opções:', error)
    }
  }, [conversationId, saveSolutionOptions, messageHasOptions])

  // Helper: Ensure minimum delay for UX
  const ensureMinimumDelay = useCallback(async (startTime: number) => {
    const elapsedTime = Date.now() - startTime
    const remainingDelay = Math.max(0, MIN_DELAY_MS - elapsedTime)
    if (remainingDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingDelay))
    }
  }, [])

  // Helper: Find latest AI message from backend messages
  const findLatestAIMessage = useCallback((messages: ChatMessage[]): ChatMessage | undefined => {
    return [...messages].reverse().find(msg => msg.role === 'assistant')
  }, [])

  // Helper: Build response message object
  const buildResponseMessage = useCallback((latestAIMessage: ChatMessage | undefined) => {
    return {
      message: latestAIMessage?.content || '',
      type: 'general' as const,
      timestamp: latestAIMessage?.timestamp,
    }
  }, [])

  // Helper: Handle send message errors
  const handleSendMessageError = useCallback((error: any) => {
    setIsTyping(false)
    const errorMessage: ChatMessage = {
      role: 'assistant',
      content: `Desculpe, ocorreu um erro: ${error.message || 'Erro ao processar sua mensagem'}`,
      timestamp: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, errorMessage])
  }, [])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) {
        console.log('sendMessage: Retornando cedo - content:', content.trim(), 'isLoading:', isLoading)
        return
      }

      console.log('sendMessage: Iniciando envio da mensagem:', content.trim())

      // Handle rejection of options if applicable
      await checkAndHandleRejection(content)

      // CRÍTICO: Adicionar mensagem do usuário ao estado ANTES de fazer a chamada
      // Isso garante que a mensagem apareça mesmo se a chamada falhar
      const messageCountBeforeSend = messages.length
      const userMessage: ChatMessage = {
        role: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMessage])
      console.log('sendMessage: Mensagem do usuário adicionada ao estado')

      // Set loading state - this blocks the input immediately
      setIsLoading(true)
      setIsTyping(true)
      const startTime = Date.now()
      console.log('sendMessage: Estado de loading ativado')
      console.log('sendMessage: conversationId atual:', conversationId)
      console.log('sendMessage: workOrderId:', workOrderContext?.workOrderId)

      try {
        console.log('sendMessage: Enviando mensagem para o backend...')
        console.log('sendMessage: Conteúdo da mensagem:', content.trim())
        console.log('sendMessage: Vai chamar sendMessageToBackend agora...')
        
        // Send message to backend (backend will save user message and return all messages)
        const responseData = await sendMessageToBackend(content)
        console.log('sendMessage: ✅ Resposta recebida do backend:', responseData)
        console.log('sendMessage: conversationId retornado:', responseData?.conversationId)
        console.log('sendMessage: Número de mensagens retornadas:', responseData?.messages?.length)

        // Ensure minimum delay for UX
        await ensureMinimumDelay(startTime)
        setIsTyping(false)

        // Process backend response
        if (!responseData) {
          console.error('sendMessage: ❌ Resposta do backend é null ou undefined')
          handleSendMessageError(new Error('Resposta inválida do servidor'))
          return { message: '', type: 'general' }
        }

        if (!responseData.messages || !Array.isArray(responseData.messages)) {
          console.error('sendMessage: ❌ Resposta não contém array de mensagens:', responseData)
          console.error('sendMessage: responseData.messages type:', typeof responseData.messages)
          console.error('sendMessage: responseData.messages value:', responseData.messages)
          // Se não há mensagens, manter a mensagem do usuário mas mostrar erro
          handleSendMessageError(new Error('Resposta inválida do servidor - messages não é array'))
          return { message: '', type: 'general' }
        }

        console.log('sendMessage: Mensagens brutas do backend (antes de converter):', responseData.messages.length, responseData.messages)
        console.log('sendMessage: responseData completo:', JSON.stringify(responseData, null, 2))
        
        const backendMessages = convertBackendMessages(responseData.messages)
        console.log('sendMessage: Mensagens convertidas do backend:', backendMessages.length)
        backendMessages.forEach((m, idx) => {
          console.log(`sendMessage: Mensagem ${idx}: role=${m.role}, content=${m.content?.substring(0, 50)}...`)
        })
        
        // CRÍTICO: Verificar se a mensagem do usuário está nas mensagens do backend
        // Se não estiver, adicionar manualmente para garantir que aparece
        const userMessageInBackend = backendMessages.some(msg => 
          msg.role === 'user' && msg.content && msg.content.trim() === content.trim()
        )
        console.log('sendMessage: Mensagem do usuário encontrada no backend?', userMessageInBackend)
        if (!userMessageInBackend) {
          console.warn('sendMessage: ⚠️ Mensagem do usuário NÃO encontrada nas mensagens do backend!')
          console.warn('sendMessage: Mensagem esperada:', content.trim())
          console.warn('sendMessage: Mensagens do backend:', backendMessages.map(m => ({ role: m.role, content: m.content?.substring(0, 100) })))
        }
        
        // Update messages state - backend returns all messages including the user's and AI's
        // Substituir todas as mensagens (incluindo a que adicionamos acima) pelas do backend
        // CRÍTICO: Usar o estado atual das mensagens (que inclui a mensagem do usuário que adicionamos)
        setMessages((currentMessages) => {
          console.log('sendMessage: Estado atual das mensagens antes de atualizar:', currentMessages.length)
          let finalMessages = updateMessagesWithBackendData(backendMessages, currentMessages)
          console.log('sendMessage: Mensagens após updateMessagesWithBackendData:', finalMessages.length)
        
          // Se a mensagem do usuário não estiver no backend, garantir que está no estado final
          if (!userMessageInBackend) {
            console.warn('⚠️ Mensagem do usuário não encontrada no backend, adicionando manualmente')
            const userMsg: ChatMessage = {
              role: 'user',
              content: content.trim(),
              timestamp: new Date().toISOString(),
              _alreadyDisplayed: true,
              _isHistorical: false, // Nova mensagem, não histórico
            }
            // Adicionar antes da última mensagem (que deve ser a resposta da IA)
            const lastIndex = finalMessages.length - 1
            if (lastIndex >= 0 && finalMessages[lastIndex].role === 'assistant') {
              finalMessages = [...finalMessages.slice(0, lastIndex), userMsg, finalMessages[lastIndex]]
              console.log('sendMessage: Mensagem do usuário inserida antes da resposta da IA')
            } else {
              finalMessages = [...finalMessages, userMsg]
              console.log('sendMessage: Mensagem do usuário adicionada ao final')
            }
          }
          
          // Garantir que temos pelo menos a mensagem do usuário
          const hasUserMessage = finalMessages.some(msg => msg.role === 'user' && msg.content === content.trim())
          if (!hasUserMessage) {
            console.error('sendMessage: ❌ CRÍTICO: Mensagem do usuário ainda não está no estado final!')
            console.error('sendMessage: Adicionando mensagem do usuário como fallback de emergência')
            const userMsg: ChatMessage = {
              role: 'user',
              content: content.trim(),
              timestamp: new Date().toISOString(),
              _alreadyDisplayed: true,
              _isHistorical: false,
            }
            finalMessages = [userMsg, ...finalMessages]
          }
          
          console.log('sendMessage: Mensagens finais antes de atualizar estado:', finalMessages.length)
          finalMessages.forEach((m, idx) => {
            console.log(`sendMessage: Mensagem final ${idx}: role=${m.role}, content=${m.content?.substring(0, 50)}...`)
          })
          return finalMessages
        })
        
        // Extract solution options if present
        const latestAIMessage = findLatestAIMessage(backendMessages)
        await extractAndSaveOptions(backendMessages, latestAIMessage)
        
        return buildResponseMessage(latestAIMessage)
      } catch (error: any) {
        const isTimeout = error?.code === 'ECONNABORTED' || (error?.message && String(error.message).toLowerCase().includes('timeout'))
        console.error('sendMessage: ❌ Erro:', isTimeout ? 'timeout' : error?.message, error)

        // Se foi timeout, o backend pode ainda estar a processar. Polling até a resposta aparecer.
        if (isTimeout && workOrderContext?.workOrderId && messageCountBeforeSend >= 0) {
          const minMessageCount = messageCountBeforeSend + 2 // user + assistant
          console.log('sendMessage: ⏳ Timeout – a aguardar resposta da IA (polling)...')
          for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
            try {
              const listRes = await api.get<{ data: Array<{ conversationId?: number; id?: number; conversation_id?: number; dtChanged?: string }> }>(
                `/chat/mechanic/work-order/${workOrderContext.workOrderId}`,
                { timeout: 10000 }
              )
              const conversations = listRes.data?.data || []
              if (conversations.length === 0) continue
              const sorted = [...conversations].sort((a, b) => {
                const tA = (a as any).dtChanged ? new Date((a as any).dtChanged).getTime() : 0
                const tB = (b as any).dtChanged ? new Date((b as any).dtChanged).getTime() : 0
                return tB - tA
              })
              const convId = sorted[0].conversationId ?? sorted[0].id ?? (sorted[0] as any).conversation_id
              if (!convId) continue
              const convRes = await api.get<{ data: { conversationId: number; messages: any[] } }>(
                `/chat/mechanic/${convId}`,
                { timeout: 10000 }
              )
              const list = convRes.data?.data?.messages
              if (!list || list.length < minMessageCount) continue
              const backendMessages = convertBackendMessages(list)
              setConversationId(convId)
              setMessages((current) => updateMessagesWithBackendData(backendMessages, current))
              const latestAI = findLatestAIMessage(backendMessages)
              await extractAndSaveOptions(backendMessages, latestAI)
              setIsTyping(false)
              console.log('sendMessage: ✅ Resposta da IA obtida por polling')
              return
            } catch (_) {
              /* ignorar erro do poll, tentar de novo */
            }
          }
          console.warn('sendMessage: Polling terminou sem nova resposta')
        }

        setIsTyping(false)
        handleSendMessageError(error)
      } finally {
        // Sempre desbloquear o input no final (sucesso ou erro)
        // A mensagem do usuário já foi adicionada ao estado antes da chamada
        console.log('sendMessage: Desbloqueando input (finally)')
        setIsLoading(false)
      }
    },
    [isLoading, messages, workOrderContext, checkAndHandleRejection, sendMessageToBackend, ensureMinimumDelay, convertBackendMessages, updateMessagesWithBackendData, extractAndSaveOptions, findLatestAIMessage, buildResponseMessage, handleSendMessageError]
  )

  const clearMessages = useCallback(() => {
    setMessages([
      {
        role: 'assistant',
        content: 'Está com problemas? Posso ajudá-lo com um passo a passo para esta viatura.',
        timestamp: new Date().toISOString(),
      },
    ])
  }, [])

  const extractWorkOrderData = useCallback(async () => {
    const currentHistory = messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
    }))
    return aiService.extractWorkOrderData(currentHistory)
  }, [messages])

  const selectSolutionOption = useCallback(async (optionId: number) => {
    if (!conversationId) {
      throw new Error('Conversa não encontrada')
    }

    try {
      await api.put(`/chat/mechanic/${conversationId}/solution-options/${optionId}/select`, undefined, { timeout: CHAT_AI_TIMEOUT_MS })
      // Update local state: selected option becomes status 2, others with status 1 become status 3
      setSolutionOptions((prev) =>
        prev.map((opt) => {
          if (opt.id === optionId) {
            return { ...opt, status: 2, isSelected: true } // Confirmada
          } else if (opt.status === 1) {
            return { ...opt, status: 3, isSelected: false } // Rejeitada
          }
          return opt
        })
      )
    } catch (error) {
      console.error('Erro ao selecionar opção:', error)
      throw error
    }
  }, [conversationId])

  const rejectAllSolutionOptions = useCallback(async () => {
    if (!conversationId) {
      console.warn('Tentativa de rejeitar opções sem conversationId')
      return
    }

    try {
      await api.put(`/chat/mechanic/${conversationId}/solution-options/reject-all`, undefined, { timeout: CHAT_AI_TIMEOUT_MS })
      // Update local state: all options with status 1 become status 3
      setSolutionOptions((prev) =>
        prev.map((opt) => (opt.status === 1 ? { ...opt, status: 3, isSelected: false } : opt))
      )
    } catch (error) {
      console.error('Erro ao rejeitar opções:', error)
      // Don't throw, just log - user can still continue conversation
    }
  }, [conversationId])

  const submitFeedback = useCallback(async (messageId: number, used: boolean, worked?: boolean) => {
    if (!messageId) {
      console.warn('Tentativa de enviar feedback sem messageId')
      return
    }

    try {
      const createdBy = user?.email || user?.name || 'system'
      
      // Enviar o feedback
      await api.post(`/chat/mechanic/message/${messageId}/feedback`, {
        used,
        worked: used ? worked : null, // Only set worked if used is true
        updatedBy: createdBy,
      }, { timeout: CHAT_AI_TIMEOUT_MS })

      // Update local state with feedback WITHOUT triggering typewriter effect
      // Preserve the _alreadyDisplayed flag to prevent re-showing typewriter effect
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.messageId === messageId) {
            return { 
              ...msg, 
              feedbackUsed: used, 
              feedbackWorked: used ? worked : null,
              _alreadyDisplayed: true // Mark as already displayed to prevent typewriter effect
            }
          }
          return msg
        })
      )
    } catch (error) {
      console.error('Erro ao enviar feedback:', error)
      throw error
    }
  }, [user])

  // Função separada para solicitar resposta detalhada (chamada após modal de confirmação)
  const requestDetailedResponse = useCallback(async (messageId: number) => {
    if (!conversationId) {
      console.warn('Tentativa de solicitar resposta detalhada sem conversationId')
      return
    }

    const createdBy = user?.email || user?.name || 'system'
    
    // Encontrar a mensagem que não foi usada para incluir no contexto
    const rejectedMessage = messages.find(msg => msg.messageId === messageId)
    
    if (!rejectedMessage) {
      console.warn('Mensagem rejeitada não encontrada')
      return
    }

    console.log('🔄 Solicitando resposta detalhada com IA real...')
    
    // Ativar loading
    setIsLoading(true)
    setIsTyping(true)
    
    try {
      // Chamar endpoint especial que força uso de Gemini/Custom model
      const response = await api.post<{ data: { conversationId: number; messages: any[] } }>(
        `/chat/mechanic/${conversationId}/regenerate`,
        {
          messageId,
          rejectedResponse: rejectedMessage.content,
          createdBy,
          forceDetailedAI: true, // Flag para forçar uso de IA real (Gemini/Custom)
        },
        { timeout: CHAT_AI_TIMEOUT_MS }
      )

      console.log('✅ Resposta detalhada recebida:', response.data)

      // Processar resposta
      if (response.data?.data?.messages) {
        const backendMessages = convertBackendMessages(response.data.data.messages)
        
        setMessages((currentMessages) => {
          const finalMessages = updateMessagesWithBackendData(backendMessages, currentMessages)
          return finalMessages
        })
      } else {
        // Se não houver mensagens na resposta, mostrar erro
        const errorMessage: ChatMessage = {
          role: 'assistant',
          content: '⚠️ Desculpe, não foi possível gerar uma resposta mais detalhada neste momento. Por favor, tente enviar uma nova mensagem com mais detalhes sobre o problema.',
          timestamp: new Date().toISOString(),
        }
        setMessages((prev) => [...prev, errorMessage])
      }
    } catch (regenerateError) {
      console.error('Erro ao regenerar resposta:', regenerateError)
      // Adicionar mensagem de erro
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: '⚠️ Desculpe, não foi possível gerar uma resposta mais detalhada neste momento. Por favor, tente enviar uma nova mensagem com mais detalhes sobre o problema.',
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
      setIsTyping(false)
    }
  }, [user, conversationId, messages, convertBackendMessages, updateMessagesWithBackendData])

  return {
    messages,
    isLoading,
    isTyping,
    sendMessage,
    clearMessages,
    extractWorkOrderData,
    solutionOptions,
    selectSolutionOption,
    rejectAllSolutionOptions,
    conversationId,
    submitFeedback,
    requestDetailedResponse,
  }
}


